import { useTranslations } from "adapters/i18n";
import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import type { Corral } from "shared/types";
import type { CorralFormState } from "../corral.types";
import {
  CorralNameError,
  validateCorralName,
  validateRetreatId,
  validateShortName,
} from "../utils/corral.validators";

type UseCorralFormProps = {
  corrals: Corral[];
  onSubmit: (formState: CorralFormState) => void;
  selectedCorral: Corral | null;
};

export function useCorralForm({
  corrals,
  onSubmit,
  selectedCorral,
}: UseCorralFormProps) {
  const { t } = useTranslations();

  const [formState, setFormState] = useState<CorralFormState>({
    name: { value: selectedCorral?.name ?? "", error: null },
    retreatId: { value: selectedCorral?.retreatId ?? null, error: null },
    shortName: { value: selectedCorral?.shortName ?? "", error: null },
  });

  const nameErrorToMessage: Record<CorralNameError, string> = {
    [CorralNameError.Required]: t("common:validation.required-field"),
    [CorralNameError.Duplicated]: t("locales:duplicated-corral-name-error"),
  };

  const handleSelectRetreat = (value: number | null) => {
    setFormState((prevState) => ({
      ...prevState,
      retreatId: { value, error: null },
    }));
  };

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const { error } = validateCorralName(
      value,
      corrals,
      selectedCorral?.id ?? null
    );
    const errorMessage = error != null ? nameErrorToMessage[error] : null;

    setFormState((prevState) => ({
      ...prevState,
      name: { value, error: errorMessage },
    }));
  };

  const handleChangeShortName = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormState((prevState) => ({
      ...prevState,
      shortName: { value, error: null },
    }));
  };

  const handleResetForm = () => {
    setFormState({
      name: { value: "", error: null },
      retreatId: { value: null, error: null },
      shortName: { value: "", error: null },
    });
  };

  const canSubmitForm = useMemo(() => {
    const { isValid: isNameValid } = validateCorralName(
      formState.name.value,
      corrals,
      selectedCorral?.id ?? null
    );

    const { isValid: isRetreatIdValid } = validateRetreatId(
      formState.retreatId.value
    );

    const { isValid: isShortNameValid } = validateShortName(
      formState.shortName.value
    );

    return isNameValid && isRetreatIdValid && isShortNameValid;
  }, [
    corrals,
    formState.name.value,
    formState.retreatId.value,
    formState.shortName.value,
    selectedCorral?.id,
  ]);

  const handleSubmitForm = useCallback(async () => {
    if (!canSubmitForm) {
      return null;
    }

    onSubmit(formState);
  }, [canSubmitForm, formState, onSubmit]);

  return {
    canSubmitForm,
    formState,
    handleChangeName,
    handleChangeShortName,
    handleResetForm,
    handleSelectRetreat,
    handleSubmitForm,
  };
}
